import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, Paper, Chip, IconButton } from '@mui/material';
import { MapContainer, TileLayer, Polygon, useMapEvents, Marker, Popup } from 'react-leaflet';
import { LatLngExpression, LatLng } from 'leaflet';
import { Delete, Add, Save } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface AreaPolygon {
  id: string;
  name: string;
  coordinates: LatLngExpression[] | number[][];
}

// Helper function to normalize coordinates
const normalizeCoordinates = (coords: LatLngExpression[] | number[][]): LatLngExpression[] => {
  if (!coords || coords.length === 0) return [];
  return coords.map(coord => {
    if (Array.isArray(coord) && coord.length >= 2) {
      return [coord[0], coord[1]] as LatLngExpression;
    }
    return coord as LatLngExpression;
  });
};

interface AreaSelectionMapProps {
  initialAreas?: AreaPolygon[];
  onSave?: (areas: AreaPolygon[]) => void;
  onCancel?: () => void;
  providerName?: string;
  readOnly?: boolean;
}

// Component to handle map clicks for drawing polygons
const MapClickHandler: React.FC<{
  isDrawing: boolean;
  currentPolygon: LatLngExpression[];
  onPointAdd: (point: LatLngExpression) => void;
  onPolygonComplete: () => void;
}> = ({ isDrawing, currentPolygon, onPointAdd, onPolygonComplete }) => {
  useMapEvents({
    click: (e) => {
      if (isDrawing) {
        onPointAdd([e.latlng.lat, e.latlng.lng]);
      }
    },
  });
  return null;
};

const AreaSelectionMap: React.FC<AreaSelectionMapProps> = ({
  initialAreas = [],
  onSave,
  onCancel,
  providerName = 'Service Provider',
  readOnly = false,
}) => {
  const [areas, setAreas] = useState<AreaPolygon[]>(initialAreas);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPolygon, setCurrentPolygon] = useState<LatLngExpression[]>([]);
  const [currentAreaName, setCurrentAreaName] = useState('');
  const [editingAreaId, setEditingAreaId] = useState<string | null>(null);

  // Default center - Muscat, Oman
  const defaultCenter: LatLngExpression = [23.5859, 58.4059]; // Muscat, Oman
  const defaultZoom = 11;

  useEffect(() => {
    if (initialAreas.length > 0) {
      console.log('Loading initial areas:', initialAreas);
      setAreas(initialAreas);
    } else {
      console.log('No initial areas provided');
      setAreas([]);
    }
  }, [initialAreas]);

  const handlePointAdd = (point: LatLngExpression) => {
    setCurrentPolygon([...currentPolygon, point]);
  };

  const handleStartDrawing = () => {
    setIsDrawing(true);
    setCurrentPolygon([]);
    setCurrentAreaName('');
    setEditingAreaId(null);
  };

  const handleCancelDrawing = () => {
    setIsDrawing(false);
    setCurrentPolygon([]);
    setCurrentAreaName('');
    setEditingAreaId(null);
  };

  const handleCompletePolygon = () => {
    if (currentPolygon.length >= 3 && currentAreaName.trim()) {
      // Ensure coordinates are in [lat, lng] format
      const normalizedCoords = currentPolygon.map(coord => {
        if (Array.isArray(coord)) {
          return [Number(coord[0]), Number(coord[1])] as [number, number];
        }
        if (typeof coord === 'object' && coord !== null && 'lat' in coord && 'lng' in coord) {
          return [Number((coord as any).lat), Number((coord as any).lng)] as [number, number];
        }
        return coord;
      });
      
      // Close the polygon by adding the first point at the end
      const closedCoords = [...normalizedCoords, normalizedCoords[0]];
      
      const newArea: AreaPolygon = {
        id: editingAreaId || `area-${Date.now()}`,
        name: currentAreaName.trim(),
        coordinates: closedCoords,
      };
      
      console.log('Completing polygon:', newArea);
      
      if (editingAreaId) {
        setAreas(areas.map(a => a.id === editingAreaId ? newArea : a));
      } else {
        setAreas([...areas, newArea]);
      }
      
      setCurrentPolygon([]);
      setCurrentAreaName('');
      setIsDrawing(false);
      setEditingAreaId(null);
    } else {
      console.warn('Cannot complete polygon:', {
        pointCount: currentPolygon.length,
        hasName: !!currentAreaName.trim()
      });
    }
  };

  const handleDeleteArea = (areaId: string) => {
    setAreas(areas.filter(a => a.id !== areaId));
  };

  const handleEditArea = (area: AreaPolygon) => {
    setEditingAreaId(area.id);
    const normalized = normalizeCoordinates(area.coordinates);
    setCurrentPolygon(normalized.slice(0, -1)); // Remove last point (duplicate of first)
    setCurrentAreaName(area.name);
    setIsDrawing(true);
    setAreas(areas.filter(a => a.id !== area.id));
  };

  const handleSave = () => {
    if (onSave) {
      // Ensure we have areas with valid coordinates
      if (areas.length === 0) {
        console.warn('No areas to save');
        return;
      }
      
      // Pass raw areas - let the parent component handle transformation
      // Just ensure we only pass areas with valid coordinates
      const validAreas = areas.filter(area => {
        const hasValidCoords = area.coordinates && 
          Array.isArray(area.coordinates) && 
          area.coordinates.length >= 3;
        if (!hasValidCoords) {
          console.warn('Area has invalid coordinates:', area);
        }
        return hasValidCoords;
      });
      
      if (validAreas.length === 0) {
        console.warn('No valid areas to save');
        return;
      }
      
      console.log('Saving areas (raw):', validAreas);
      onSave(validAreas);
    }
  };

  const getPolygonCenter = (coordinates: LatLngExpression[] | number[][]): LatLngExpression => {
    const normalized = normalizeCoordinates(coordinates);
    if (normalized.length === 0) return defaultCenter;
    const validCoords = normalized.filter(c => Array.isArray(c) && c.length >= 2) as number[][];
    if (validCoords.length === 0) return defaultCenter;
    
    const sum = validCoords.reduce(
      (acc, coord) => [acc[0] + coord[0], acc[1] + coord[1]],
      [0, 0]
    );
    return [sum[0] / validCoords.length, sum[1] / validCoords.length];
  };

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Select Areas for {providerName}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Click "Start Drawing" to begin, then click on the map to add points. 
          Add at least 3 points and enter an area name, then click "Complete Polygon".
        </Typography>

        {!readOnly && (
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
            {!isDrawing ? (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleStartDrawing}
              >
                Start Drawing New Area
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelDrawing}
                >
                  Cancel Drawing
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleCompletePolygon}
                  disabled={currentPolygon.length < 3 || !currentAreaName.trim()}
                >
                  Complete Polygon ({currentPolygon.length} points)
                </Button>
              </>
            )}
          </Stack>
        )}

        {!readOnly && isDrawing && (
          <Box sx={{ mb: 2 }}>
            <input
              type="text"
              placeholder="Enter area name (e.g., Downtown, North District)"
              value={currentAreaName}
              onChange={(e) => setCurrentAreaName(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                maxWidth: '400px',
                fontSize: '14px',
              }}
            />
            {currentPolygon.length > 0 && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Points added: {currentPolygon.length} (minimum 3 required)
              </Typography>
            )}
          </Box>
        )}

        {areas.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {readOnly ? 'Service Areas' : 'Selected Areas'} ({areas.length}):
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {areas.map((area) => (
                <Chip
                  key={area.id}
                  label={area.name}
                  onDelete={readOnly ? undefined : () => handleDeleteArea(area.id)}
                  onClick={readOnly ? undefined : () => handleEditArea(area)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      <Box sx={{ height: '500px', width: '100%', position: 'relative', border: '1px solid #ddd', borderRadius: 1 }}>
        <MapContainer
          center={defaultCenter}
          zoom={defaultZoom}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {!readOnly && (
            <MapClickHandler
              isDrawing={isDrawing}
              currentPolygon={currentPolygon}
              onPointAdd={handlePointAdd}
              onPolygonComplete={handleCompletePolygon}
            />
          )}

          {/* Render existing areas */}
          {areas.map((area) => {
            const normalizedCoords = normalizeCoordinates(area.coordinates);
            if (normalizedCoords.length === 0) return null;
            return (
              <React.Fragment key={area.id}>
                <Polygon
                  positions={normalizedCoords}
                  pathOptions={{
                    color: '#3388ff',
                    fillColor: '#3388ff',
                    fillOpacity: 0.3,
                    weight: 2,
                  }}
                />
                <Marker position={getPolygonCenter(normalizedCoords)}>
                  <Popup>{area.name}</Popup>
                </Marker>
              </React.Fragment>
            );
          })}

          {/* Render current drawing polygon */}
          {isDrawing && currentPolygon.length > 0 && (
            <Polygon
              positions={[...currentPolygon, currentPolygon[0]]}
              pathOptions={{
                color: '#ff0000',
                fillColor: '#ff0000',
                fillOpacity: 0.2,
                weight: 2,
                dashArray: '5, 5',
              }}
            />
          )}

          {/* Show markers for current polygon points */}
          {isDrawing && currentPolygon.map((point, index) => (
            <Marker key={index} position={point}>
              <Popup>Point {index + 1}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </Box>

      {!readOnly && (
        <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'flex-end' }}>
          {onCancel && (
            <Button variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={areas.length === 0}
          >
            Save Areas {areas.length > 0 && `(${areas.length})`}
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default AreaSelectionMap;
export type { AreaPolygon };
